import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Optional, output, Self, signal, TemplateRef, ViewChild, ViewContainerRef, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { AddressEditModel } from '@shared/models/address-edit.model';
import { AddressSearchResult, AddressVerifyPayload, defaultAddressVerifyOptions } from '@shared/models/address-typeahead.model';
import { TypeaheadService } from '@shared/services/typeahead.service';
import { BaseMatFormFieldControl } from '@shared/util/base-mat-form-field-control';
import { BehaviorSubject, catchError, debounceTime, distinctUntilChanged, finalize, map, of, switchMap } from 'rxjs';
import { addressValidationErrorsToFormErrors, filterNullishValues, getAddressValidationErrors, highlightMatch } from '../add-new-company-dialog.utils';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';

@Component({
  selector: 'add-new-company-address-autocomplete',
  imports: [
    MatInput,
    MatIcon,
    MatButton,
    MatAutocompleteModule,
    AsyncPipe,
    TranslocoPipe
  ],
  templateUrl: './add-new-company-address-autocomplete.component.html',
  styleUrl: './add-new-company-address-autocomplete.component.scss',
  providers: [
    provideTranslocoScope('sharedDialogs'),
    {
      provide: MatFormFieldControl,
      useExisting: AddNewCompanyAddressAutocompleteComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddNewCompanyAddressAutocompleteComponent extends BaseMatFormFieldControl<string> implements OnInit {
  @ViewChild('inputElement', { static: true })
  public inputElement!: ElementRef<HTMLInputElement>;

  @ViewChild('autocompleteTemplate', { static: true })
  public autocompleteTemplate!: TemplateRef<any>;

  public addAddressClicked = output<void>();
  public inputTextChange = output<string>();
  public searchInProgress = output<boolean>();

  public inputValue = signal('');
  public inputValue$ = toObservable(this.inputValue);

  private addressResultsSubject = new BehaviorSubject<AddressEditModel[]>([]);
  public addressOptions$ = this.addressResultsSubject.asObservable();

  private overlayRef: OverlayRef | null = null;
  private blurCloseTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private viewContainerRef: ViewContainerRef,
    private typeaheadService: TypeaheadService
  ) {
    super(ngControl);
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  public focused = false;

  override get errorState(): boolean {
    return !!this.ngControl?.invalid;
  }

  public ngOnInit(): void {
    this.inputValue$
      .pipe(
        debounceTime(300),
        switchMap(fullAddress => {
          if (fullAddress.length < 3) {
            this.searchInProgress.emit(false);
            return of([]);
          }
          const payload: AddressVerifyPayload = {
            fullAddress,
            ...defaultAddressVerifyOptions,
            includeSpaceMatches: true,
            includeAssetMatches: true,
          };
          return this.typeaheadService.getAddressMatches(payload).pipe(
            debounceTime(300),
            distinctUntilChanged(),
            map((results: AddressSearchResult[]) => results.map(result => result.address)),
            catchError(() => of([])),
            finalize(() => this.searchInProgress.emit(false))
          );
        })
      )
      .subscribe(addresses => {
        this.addressResultsSubject.next(addresses);
      });
  }

  public focus(): void {
    if (this.inputElement) {
      this.inputElement.nativeElement.focus();
    }
  }

  public onInputTextChange(target: EventTarget | null): void {
    if (target instanceof HTMLInputElement) {
      this.inputValue.set(target.value);
      this.inputTextChange.emit(target.value);

      this.searchInProgress.emit(this.inputValue().length > 2);

      if (this.inputValue().length >= 3) {
        this.openOverlay();
      } else {
        this.closeOverlay();
        this.addressResultsSubject.next([]);
      }

      this.stateChanges.next();
    }
  }

  public selectAddress(address: AddressEditModel): void {
    if (this.blurCloseTimeout) {
      clearTimeout(this.blurCloseTimeout);
      this.blurCloseTimeout = null;
    }

    const fullAddress = filterNullishValues(address);
    address.fullAddress = fullAddress;

    const validationErrors = getAddressValidationErrors(address);
    address.error = validationErrors.length ? validationErrors : undefined;

    this.onChange(address);
    this.writeValue(fullAddress);
    this.inputValue.set(fullAddress);
    this.ngControl?.control?.setErrors(addressValidationErrorsToFormErrors(validationErrors));
    this.stateChanges.next();
    if (validationErrors.length > 0) {
      this.addAddressClicked.emit();
    }
    this.closeOverlay();
  }

  public highlightAddressMatch(address: AddressEditModel): string {
    const fullAddress = filterNullishValues(address);

    return highlightMatch(fullAddress, this.inputValue());
  }

  public addNew(): void {
    if (this.blurCloseTimeout) {
      clearTimeout(this.blurCloseTimeout);
      this.blurCloseTimeout = null;
    }

    this.onChange({
      streetAddress: this.inputValue(),
    } as AddressEditModel);
    this.ngControl?.control?.setErrors(null);
    this.stateChanges.next();

    this.addAddressClicked.emit();
    this.closeOverlay();
  }

  public onOverlayMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  public onContainerClick(event: MouseEvent): void {
    this.focus();
  }

  public onInputFocus(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
    if (this.inputValue().length >= 3) {
      this.openOverlay();
    }
  }

  public onInputBlur(): void {
    this.touched = true;
    this.focused = false;
    this.onTouched();
    this.stateChanges.next();
    this.blurCloseTimeout = setTimeout(() => {
      this.closeOverlay();
      this.blurCloseTimeout = null;
    }, 200);
  }

  private openOverlay(): void {
    if (this.overlayRef) {
      return;
    }

    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.inputElement)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
          offsetY: 14,
          offsetX: -10,
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
          offsetY: -4,
          offsetX: 10,
        },
      ])
      .withPush(false)
      .withViewportMargin(8);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: false,
      width: 740,
      minWidth: 740,
    });

    const templatePortal = new TemplatePortal(
      this.autocompleteTemplate,
      this.viewContainerRef
    );

    this.overlayRef.attach(templatePortal);
  }

  private closeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
